import AddIcon from "@mui/icons-material/Add";
import {
  ClickAwayListener,
  Grow,
  Link,
  MenuItem,
  Paper,
  Popper,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import RosterApi from "../../../axios/Roster/RosterApi";
import { CustomAdvancedMenuList } from "../../../components/StyledComponents";
import { useLoading } from "../../../context/LoadingContext";
import AddRosterDialog from "./AddRosterDialog";
import DeleteRosterDialog from "./DeleteRosterDialog";
import EditRosterDialog from "./EditRosterDialog";
import { useAuth } from "../../../context/AuthContext";
import EditIcon16pxSvg from "../../../assets/images/icons/EditIcon16pxSvg";
import DeleteIcon16pxSvg from "../../../assets/images/icons/DeleteIcon16pxSvg";
import MoreIcon16pxSvg from "../../../assets/images/icons/MoreIcon16pxSvg";
import CustomSplitButton from "../../../components/CustomSplitButton";
import ImportIcon16pxSvg from "../../../assets/images/icons/ImportIcon16pxSvg";
import ExportIcon16pxSvg from "../../../assets/images/icons/ExportIcon16pxSvg";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";

const MODE = {
  none: -1,
  advanced: 1,
};

/** Roster giả khi tour mà công ty chưa có dữ liệu */
export const TOUR_FAKE_ROSTER = {
  rosterId: "tour-fake-roster",
  rosterName: "Sample Roster",
  repetition: 1,
  startDate: "2026-01-01",
  endBy: null,
  formatStartDate: "01/01/2026",
  formatEndBy: "",
  range: 7,
};

/* =========================
   Roster Card
========================= */
const RosterCard = ({
  roster,
  selected,
  onSelect,
  onEdit,
  onDelete,
  tourTarget,
  forceMenuOpen = false,
}) => {
  const { t } = useTranslation();
  const { isEmployee, isAdmin } = useAuth();
  const [openMenu, setOpenMenu] = useState(false);
  // State anchor — tránh Popper mở khi ref còn null (menu bay góc trái)
  const [anchorEl, setAnchorEl] = useState(null);

  const setAnchorRef = (node) => {
    setAnchorEl(node);
  };

  const menuOpen = Boolean(anchorEl) && (forceMenuOpen || openMenu);

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpenMenu((prev) => !prev);
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    setOpenMenu(false);
  };

  return (
    <div
      onClick={onSelect}
      data-tour={tourTarget ? "shift-and-roster-roster-data" : undefined}
      className={`
        group
        relative rounded-xl border p-4 cursor-pointer transition 
        ${selected
          ? "border-[#0057FF] bg-[#F5F8FF]"
          : "border-[#E5E7EB] hover:border-[#0057FF] hover:bg-[#F5F8FF]"
        }
      `}
    >
      <div className="flex justify-between items-start gap-3">
        <div>
          <p
            className={`
    font-semibold text-base transition-colors
    ${selected ? "text-[#0057FF]" : "text-[#1F2937] group-hover:text-[#0057FF]"}
  `}
          >
            {roster.rosterName}
          </p>

          <div className="mt-2 text-sm text-gray-600 space-y-1">
            <p>
              <span className="font-medium">{t("common.repetiton")}:</span>{" "}
              {roster.repetition} {t("common.weeks")}
            </p>
            <p>
              <span className="font-medium">{t("common.start-date")}:</span>{" "}
              {roster.formatStartDate}
            </p>
            <p>
              <span className="font-medium">{t("common.end-date")}:</span>{" "}
              {roster.formatEndBy || t("common.no-end-date")}
            </p>
          </div>
        </div>

        {isAdmin && (
          <div onClick={(e) => e.stopPropagation()}>
            {/* 3 dots menu */}
            <div
              ref={setAnchorRef}
              onClick={(e) => {
                e.stopPropagation();
                handleToggle(e);
              }}
              className="
    group
    p-1
    rounded-full
    cursor-pointer
    text-[#1F2937]
    hover:text-[#0057FF]
    transition
  "
            >
              <MoreIcon16pxSvg width={16} height={16} />
            </div>

            <Popper
              open={menuOpen}
              anchorEl={anchorEl}
              placement="bottom-end"
              transition
              disablePortal
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps} timeout={window.__tourIsOpen ? 0 : undefined}>
                  <Paper elevation={3}>
                    <ClickAwayListener onClickAway={handleClose}>
                      <CustomAdvancedMenuList autoFocusItem={menuOpen}>
                        <MenuItem
                          data-tour={
                            tourTarget ? "shift-and-roster-edit-roster" : undefined
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClose();
                            onEdit();
                          }}
                          className="!text-[#1F2937]"
                          disabled={!isAdmin || isEmployee}
                        >
                          <EditIcon16pxSvg
                            width={16}
                            height={16}
                            color="#1F2937"
                            className="mr-2"
                          />
                          {t("button.edit")}
                        </MenuItem>

                        <MenuItem
                          data-tour={
                            tourTarget ? "shift-and-roster-delete-roster" : undefined
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClose();
                            onDelete();
                          }}
                          className="!text-[#D00D00]"
                          disabled={!isAdmin || isEmployee}
                        >
                          <DeleteIcon16pxSvg
                            width={16}
                            height={16}
                            color="#D00D00"
                            className="mr-2"
                          />
                          {t("button.delete")}
                        </MenuItem>
                      </CustomAdvancedMenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        )}
        {/* 3 dots menu */}
      </div>
    </div>
  );
};

/* =========================
   Main component
========================= */
const Rosters = ({
  onRowSelection,
  refreshData,
  onRefresh,
  handleActiveWeekTabIndex,
  allUser,
  onChangeAllUser,
  GroupData,
  refreshDataAddRoster,
  handleRefreshDataAddRoster,
  selectedRows,
  usersData,
  disableAutoSelect = false,
}) => {
  const { genaralSettingDateFormat, isAdmin, isEmployee, profile, dateTimeFormat } = useAuth();
  const { t } = useTranslation();
  const { getRosterListAll, exportRosterCSV } = RosterApi();
  const [show, hide] = useLoading();
  const [mode, setMode] = useState(MODE.none);
  const anchorRefAdvanced = useRef(null);

  const [rostersData, setRostersData] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  const fetchData = async () => {
    try {
      show();

      const userId = isAdmin ? null : profile?.userId;

      const response = await getRosterListAll(
        localStorage.getItem("i18nextLngId"),
        userId,
        1, // pageNo (có thể dynamic)
        20 // recordPerPage (có thể dynamic)
      );

      if (response.data?.data) {
        const mapped = response.data.data.map((roster) => ({
          ...roster,
          range: roster.repetition * 7,
          formatStartDate: roster.startDate
            ? format(new Date(roster.startDate), genaralSettingDateFormat)
            : "",
          formatEndBy: roster.endBy
            ? format(new Date(roster.endBy), genaralSettingDateFormat)
            : "",
        }));
        setRostersData(mapped);
      } else {
        setRostersData([]);
      }
    } catch (error) {
      toast.error(error);
      setRostersData([]);
    } finally {
      hide();
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshDataAddRoster, genaralSettingDateFormat, refreshData]);

  useEffect(() => {
    if (hasAutoSelected || rostersData.length === 0) return;
    if (disableAutoSelect) return;

    const firstRow = rostersData[0];
    setSelectedRowId(firstRow.rosterId);
    onRowSelection(firstRow);
    handleActiveWeekTabIndex(0);
    setHasAutoSelected(true);
  }, [rostersData, hasAutoSelected]);

  const onSelectRoster = (row) => {
    setSelectedRowId(row.rosterId);
    onRowSelection(row);
  };

  const [openAddRoster, setOpenAddRoster] = useState(false);
  const [openEditRoster, setOpenEditRoster] = useState(false);
  const [openDeleteRoster, setOpenDeleteRoster] = useState(false);
  const [selectedRoster, setSelectedRoster] = useState({});
  // Tour: hiện roster giả khi chưa có dữ liệu thật
  const [tourFakeRoster, setTourFakeRoster] = useState(false);
  const [tourForceMenuOpen, setTourForceMenuOpen] = useState(false);

  // Tour: mở/đóng Add Roster + fake roster + edit/delete
  useEffect(() => {
    window.__openAddRosterDialog = () => {
      flushSync(() => {
        setMode(MODE.none);
        setTourForceMenuOpen(false);
        setOpenEditRoster(false);
        setOpenDeleteRoster(false);
        setOpenAddRoster(true);
      });
    };
    window.__closeAddRosterDialog = () => {
      flushSync(() => setOpenAddRoster(false));
    };
    window.__enableTourFakeRoster = () => {
      flushSync(() => {
        setOpenAddRoster(false);
        setOpenEditRoster(false);
        setOpenDeleteRoster(false);
        setTourForceMenuOpen(false);
        setTourFakeRoster(true);
      });
    };
    window.__disableTourFakeRoster = () => {
      flushSync(() => {
        setTourFakeRoster(false);
        setTourForceMenuOpen(false);
        setOpenEditRoster(false);
        setOpenDeleteRoster(false);
      });
    };
    // Mở menu Edit trên roster (fake nếu chưa có data)
    // 2 phase: render card trước → chờ ref gắn → mới mở menu (tránh Popper góc trái)
    window.__openTourRosterEditMenu = () => {
      flushSync(() => {
        setOpenAddRoster(false);
        setOpenEditRoster(false);
        setOpenDeleteRoster(false);
        setTourFakeRoster(true);
        setTourForceMenuOpen(false);
        setSelectedRoster(rostersData[0] || TOUR_FAKE_ROSTER);
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          flushSync(() => setTourForceMenuOpen(true));
        });
      });
    };
    // Mở EditRosterDialog (dùng roster thật đầu tiên hoặc fake)
    window.__openTourEditRosterDialog = () => {
      flushSync(() => {
        setOpenAddRoster(false);
        setOpenDeleteRoster(false);
        setTourForceMenuOpen(false);
        setTourFakeRoster(true);
        const target = rostersData[0] || TOUR_FAKE_ROSTER;
        setSelectedRoster(target);
        setOpenEditRoster(true);
      });
    };
    window.__closeTourEditRosterDialog = () => {
      flushSync(() => setOpenEditRoster(false));
    };
    // Mở DeleteRosterDialog
    window.__openTourDeleteRosterDialog = () => {
      flushSync(() => {
        setOpenAddRoster(false);
        setOpenEditRoster(false);
        setTourForceMenuOpen(false);
        setTourFakeRoster(true);
        const target = rostersData[0] || TOUR_FAKE_ROSTER;
        setSelectedRoster(target);
        setOpenDeleteRoster(true);
      });
    };
    window.__closeTourDeleteRosterDialog = () => {
      flushSync(() => setOpenDeleteRoster(false));
    };
    return () => {
      delete window.__openAddRosterDialog;
      delete window.__closeAddRosterDialog;
      delete window.__enableTourFakeRoster;
      delete window.__disableTourFakeRoster;
      delete window.__openTourRosterEditMenu;
      delete window.__openTourEditRosterDialog;
      delete window.__closeTourEditRosterDialog;
      delete window.__openTourDeleteRosterDialog;
      delete window.__closeTourDeleteRosterDialog;
    };
  }, [rostersData]);

  const displayRosters = useMemo(() => {
    if (rostersData.length > 0) return rostersData;
    if (tourFakeRoster) return [TOUR_FAKE_ROSTER];
    return [];
  }, [rostersData, tourFakeRoster]);

  const getCurrentTimestamp = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const MM = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const HH = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
  };

  const handleCloseAdvancedMenu = () => {
    setMode(MODE.none);
  };

  const exportCSV = async () => {
    const payload = {
      langId: localStorage.getItem("i18nextLngId"),
      userId: isAdmin ? null : profile?.userId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      formatDate: dateTimeFormat,
    };

    try {
      show();
      const response = await exportRosterCSV(payload);

      if (response) {
        const blob = new Blob([response.data], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${t("export.export-roster-data")}_${getCurrentTimestamp()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        // toast.success(t("common.export-roster-success"));
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error?.response?.data));
        }
      } else {
        toast.error(t("common.network-timeout"));
      }
    } finally {
      hide();
      setMode(MODE.none);
    }
  };

  return (
    <div className="rosters">
      {isAdmin && (
        <div className="flex justify-end gap-3 my-4" >
          <CustomSplitButton
            data-tour="shift-and-roster-add-roster-button"
            label={t("button.new-roster")}
            startIcon={<AddIcon className="!text-[16px]" />}
            onMainClick={() => setOpenAddRoster(true)}
            onDropdownClick={() => setMode(MODE.advanced)}
            ref={anchorRefAdvanced}
          />
        </div>
      )}

      <div className="min-h-[68vh] flex flex-col">
        {displayRosters.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <NoRowOverlay />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {displayRosters.map((roster, index) => (
              <RosterCard
                key={roster.rosterId}
                roster={roster}
                tourTarget={index === 0}
                forceMenuOpen={index === 0 && tourForceMenuOpen}
                selected={roster.rosterId === selectedRowId}
                onSelect={() => {
                  if (roster.rosterId === "tour-fake-roster") {
                    // Tour: mở detail giả qua handler ở parent
                    window.__openTourRosterDetail?.();
                    return;
                  }
                  onSelectRoster(roster);
                }}
                onEdit={() => {
                  setSelectedRoster(
                    roster.rosterId === "tour-fake-roster"
                      ? TOUR_FAKE_ROSTER
                      : roster
                  );
                  setOpenEditRoster(true);
                }}
                onDelete={() => {
                  setSelectedRoster(
                    roster.rosterId === "tour-fake-roster"
                      ? TOUR_FAKE_ROSTER
                      : roster
                  );
                  setOpenDeleteRoster(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <AddRosterDialog
        open={openAddRoster}
        handleClose={() => setOpenAddRoster(false)}
        onRefresh={onRefresh}
        allUser={allUser}
        onChangeAllUser={onChangeAllUser}
        genaralSettingDateFormat={genaralSettingDateFormat}
        GroupData={GroupData}
        handleRefreshDataAddRoster={handleRefreshDataAddRoster}
      />

      <DeleteRosterDialog
        open={openDeleteRoster}
        handleClose={() => setOpenDeleteRoster(false)}
        selectedDeleteRoster={selectedRoster}
        onRefresh={onRefresh}
        handleRefreshDataAddRoster={handleRefreshDataAddRoster}
      />

      {openEditRoster && (
        <EditRosterDialog
          onClose={() => setOpenEditRoster(false)}
          selectedEditRoster={selectedRoster}
          onRefresh={onRefresh}
          handleRefreshDataAddRoster={handleRefreshDataAddRoster}
          GroupData={GroupData}
          allUser={allUser}
          onChangeAllUser={onChangeAllUser}
          selectedRows={selectedRows}
          usersData={usersData}
        />
      )}

      {mode === MODE.advanced && (
        <Popper
          open
          anchorEl={anchorRefAdvanced.current}
          placement="bottom-start"
          transition
          disablePortal
          className="z-10"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "left top" : "left bottom",
              }}
            >
              <Paper style={{ borderRadius: "8px", }}>
                <ClickAwayListener onClickAway={handleCloseAdvancedMenu}>
                  <CustomAdvancedMenuList
                    id="roster-advanced-menu"
                    aria-labelledby="roster-advanced-button"
                  >
                    <MenuItem
                      disabled
                      style={{
                        borderBottom: "1px solid #E5E7EB",
                        opacity: "0.5",
                        pointerEvents: "none",
                      }}
                    >
                      <Link>
                        <ImportIcon16pxSvg />
                        <p>{t("button.import-data")}</p>
                      </Link>
                    </MenuItem>
                    <MenuItem
                      style={{
                        borderBottom: "1px solid #E5E7EB",
                        pointerEvents: "auto",
                      }}
                      onClick={exportCSV}
                    >
                      <Link>
                        <ExportIcon16pxSvg />
                        <p>{t("button.export-data")}</p>
                      </Link>
                    </MenuItem>
                  </CustomAdvancedMenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  );
};

export default Rosters;
